/**
 * The possible-duplicate signal on the record page, and the kept record's read (13.2-10, D-13.2-P-10).
 *
 * **`useDuplicates(row, actor)`** finds the incidents that may describe the same event as `row`
 * (`duplicates.possibleDuplicates`). It is Oversight's signal only and never acts on its own: closing one is the
 * audited `closeAsDuplicate` verb, by a person, from the closure panel.
 *
 * - **The read, measured.** The record page reads no other incidents, so the candidates cost one extra Graph call per
 *   record open: ONE paged list read of Incidents with only `DUPLICATE_READS`, filtered on `ReportedOn` within the
 *   row's day ± 7 Melbourne days (`candidateWindow`). The list id is already cached by the record read. Because it is
 *   a second call, it is gated (`duplicateSignalApplies`): Oversight, an incident that isn't settled, that has or
 *   could have a twin (a ShiftCare ticket, or made in the last 14 days). Everyone else reads nothing (`status: 'off'`).
 * - **Once per record.** The read is keyed by the ref, not the eTag, so a save doesn't read again. The matches are
 *   worked out from the row as it is now, so a record closed as a duplicate (settled) shows none at once.
 * - **Practice records** (`INC-P<n>`): the candidates are this browser's practice incidents, never a live read; a
 *   live record never sees a practice one.
 *
 * **`DuplicatesContext`** carries the page's one result down to the closure panel, which prefills the kept ref with
 * the top match. **`useKeptRecord(ref)`** reads the record a duplicate is closed against (`ctx.kept`): the row only,
 * no siblings, after a short pause in typing.
 *
 * Tests and previews may supply their own source through `DuplicatesSourceContext`.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { candidateWindow, duplicateSignalApplies, possibleDuplicates } from '../lib/duplicates';
import type { DuplicateMatch } from '../lib/duplicates';
import { isLiveRef, isPracticeRef, parsePracticeRef, parseRef } from '../lib/recordQuery';
import { practiceRecordStore, readPracticeRecord } from '../services/commitAdapter';
import type { PracticeRecordStore } from '../services/commitAdapter';
import { errorStatus } from '../services/graphClient';
import { getRecordRow, listIncidentsNear } from '../services/recordService';
import type { RecordIO } from '../services/recordService';
import type { Actor } from '../types/actionRegister';
import type { RecordRow } from '../types/records';
import { useActionRuntime } from './useActionRuntime';

/** Where the candidates and the kept record come from: the live reads in the app, synthetic rows in tests. */
export interface DuplicatesSource {
  /** The incidents `row` may duplicate: the bounded read (live), or this browser's practice incidents (practice). */
  candidates: (row: RecordRow) => Promise<RecordRow[]>;
  /** One record's row by its ref (the kept record), or `null` when there is no such record. */
  read: (ref: string) => Promise<RecordRow | null>;
}

export const DuplicatesSourceContext = createContext<DuplicatesSource | null>(null);

const NOT_CONNECTED = 'Not connected to the Quality & Safety site yet.';

/** The app's source over `io` (null while the site connects), with practice records from this browser's store. */
export function liveDuplicatesSource(io: RecordIO | null, store?: PracticeRecordStore): DuplicatesSource {
  const practice = () => store ?? practiceRecordStore();
  return {
    candidates: async (row) => {
      if (isPracticeRef(row.ref)) {
        const s = practice();
        return s
          .list()
          .filter((r) => r.register === 'incidents')
          .map((r) => readPracticeRecord(r.ref, s)?.row)
          .filter((r): r is RecordRow => r !== undefined);
      }
      const window = candidateWindow(row);
      if (!window) return [];
      if (!io) throw new Error(NOT_CONNECTED);
      return listIncidentsNear(io, window);
    },
    read: async (ref) => {
      if (isPracticeRef(ref)) return readPracticeRecord(ref, practice())?.row ?? null;
      if (!io) throw new Error(NOT_CONNECTED);
      try {
        return await getRecordRow(io, ref);
      } catch (err) {
        if (errorStatus(err) === 404) return null;
        throw err;
      }
    },
  };
}

function useSource(): DuplicatesSource {
  const { io } = useActionRuntime();
  const given = useContext(DuplicatesSourceContext);
  return useMemo(() => given ?? liveDuplicatesSource(io as RecordIO | null), [given, io]);
}

// ---------------------------------------------------------------------------
// The signal
// ---------------------------------------------------------------------------

export interface DuplicatesResult {
  /** `off`: the signal doesn't apply to this person or record, and nothing was read. */
  status: 'off' | 'loading' | 'ready' | 'error';
  /** Best first (`possibleDuplicates`). `[]` unless `ready`. */
  matches: DuplicateMatch[];
  /** Each matched record's title as the register holds it, by ref. */
  titles: Readonly<Record<string, string>>;
  error: string | null;
}

const OFF: DuplicatesResult = { status: 'off', matches: [], titles: {}, error: null };

/** The page's one result, for the closure panel's prefill. `null` outside a record page. */
export const DuplicatesContext = createContext<DuplicatesResult | null>(null);

export interface UseDuplicatesOptions {
  /** The gate's clock (tests pin it). Default: the time the hook first ran. */
  now?: Date;
}

interface Landed {
  ref: string;
  status: 'ready' | 'error';
  candidates: RecordRow[];
  error: string | null;
}

export function useDuplicates(row: RecordRow, actor: Actor, opts: UseDuplicatesOptions = {}): DuplicatesResult {
  const source = useSource();
  const [mountedAt] = useState(() => new Date());
  const now = opts.now ?? mountedAt;
  const applies = duplicateSignalApplies(row, actor, now);
  const [landed, setLanded] = useState<Landed | null>(null);
  // The read takes the row as it was when the record opened: keyed by the ref (a save never reads again).
  const [readRow, setReadRow] = useState(row);
  if (readRow.ref !== row.ref) setReadRow(row);

  useEffect(() => {
    if (!applies) return;
    let cancelled = false;
    source
      .candidates(readRow)
      .then((candidates) => {
        if (!cancelled) setLanded({ ref: readRow.ref, status: 'ready', candidates, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLanded({ ref: readRow.ref, status: 'error', candidates: [], error: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, readRow, applies]);

  return useMemo<DuplicatesResult>(() => {
    if (!applies) return OFF;
    if (!landed || landed.ref !== row.ref) return { ...OFF, status: 'loading' };
    if (landed.status === 'error') return { ...OFF, status: 'error', error: landed.error };
    const matches = possibleDuplicates(row, landed.candidates);
    const titles: Record<string, string> = {};
    for (const m of matches) {
      const c = landed.candidates.find((r) => r.ref === m.ref);
      const t = typeof c?.fields.Title === 'string' ? c.fields.Title.trim() : '';
      if (t) titles[m.ref] = t;
    }
    return { status: 'ready', matches, titles, error: null };
  }, [applies, landed, row]);
}

// ---------------------------------------------------------------------------
// The kept record
// ---------------------------------------------------------------------------

/** How long typing pauses before the kept record is read (so "INC-1" on the way to "INC-11" is never read). */
export const KEPT_READ_PAUSE_MS = 350;

export interface KeptRecord {
  /** `idle`: nothing to read (blank, not an incident ref, or this record's own ref; the builder says why). */
  status: 'idle' | 'loading' | 'ready' | 'not-found' | 'error';
  /** The canonical ref being read, when there is one. */
  ref: string | null;
  row: RecordRow | null;
  error: string | null;
}

/**
 * The canonical incident ref a typed kept ref names, in the record's own range (`INC-P<n>` on a practice record,
 * `INC-<n>` on a live one), or `null`. The same forms `closeAsDuplicate` accepts.
 */
export function keptRefOf(row: RecordRow, typed: string): string | null {
  const value = typed.trim();
  const practice = isPracticeRef(row.ref);
  if (practice ? !isPracticeRef(value) : !isLiveRef(value)) return null;
  const parsed = practice ? parsePracticeRef(value) : parseRef(value);
  return parsed.register === row.register ? parsed.ref : null;
}

interface KeptLanded {
  ref: string;
  status: 'ready' | 'not-found' | 'error';
  row: RecordRow | null;
  error: string | null;
}

/** Reads the kept record for `ctx.kept` (the row only), once the typed ref names an incident other than `row`. */
export function useKeptRecord(row: RecordRow, typed: string): KeptRecord {
  const source = useSource();
  const ref = keptRefOf(row, typed);
  const wanted = ref !== null && ref !== row.ref ? ref : null;
  const [landed, setLanded] = useState<KeptLanded | null>(null);

  useEffect(() => {
    if (!wanted) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      source
        .read(wanted)
        .then((kept) => {
          if (!cancelled) setLanded({ ref: wanted, status: kept ? 'ready' : 'not-found', row: kept, error: null });
        })
        .catch((err: unknown) => {
          if (!cancelled) setLanded({ ref: wanted, status: 'error', row: null, error: err instanceof Error ? err.message : String(err) });
        });
    }, KEPT_READ_PAUSE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [source, wanted]);

  if (!wanted) return { status: 'idle', ref: null, row: null, error: null };
  if (!landed || landed.ref !== wanted) return { status: 'loading', ref: wanted, row: null, error: null };
  return { status: landed.status, ref: wanted, row: landed.row, error: landed.error };
}
